// src/pages/profile-settings/doctor/PracticeTeamWorkspaceHeader.jsx
import React from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X, Lock, Monitor, Moon, Sun } from "lucide-react";

import { logout } from "@/lib/auth/logout";
import AfrimedSearchBar from "@/components/ui/AfrimedSearchBar.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { getPracticeTeamMemberIdentity, getPracticeTeamNavigation } from "./practiceTeamWorkspaceModel.js";
import { usePracticeTeamMemberAccount } from "./practiceTeamMemberAccess.js";
import { updatePharmacyMemberThemePreference } from "@/services/pharmacyMemberAccountService.js";

const BRAND_LOGO = "/assets/images/afrimed_logo.svg";
const ICON_LOG_OUT_CRITICAL = "/icons/appointments/dashboard/log-out-critical.svg";
const ICON_HOME = "/icons/Practice Team/home_health.svg";
const ICON_TASKS = "/icons/description.svg";
const ICON_APPOINTMENTS = "/icons/appointments/dashboard/appointments.svg";
const ICON_MESSAGES = "/icons/list-items/messages.svg";
const ICON_PATIENTS = "/icons/medical-institution/patients.svg";
const ICON_BILLING = "/icons/request_quote.svg";
const ICON_NOTIFICATIONS = "/icons/Practice Team/notifications_bell.svg";
const ICON_ACCOUNT = "/icons/doctor/appointments/account_circle.svg";
const ICON_HELP = "/icons/Practice Team/help.svg";
const ICON_SETTINGS = "/icons/Practice Team/settings.svg";
const ICON_PRACTICE = "/icons/Practice Team/hospital.svg";
const ICON_SUGGESTION = "/icons/list-items/messages.svg";

function AssetIcon({ src, active = false, disabled = false, visualScale = "", className = "", mask = false }) {
  const iconClassName = [
    "h-6 w-6 transition",
    visualScale,
    active ? "opacity-100" : "opacity-75 group-hover:opacity-100",
    disabled ? "opacity-45 group-hover:opacity-45" : "",
    className,
  ].join(" ");

  return (
    <span className="grid h-6 w-6 shrink-0 place-items-center">
      {mask ? (
        <span
          aria-hidden="true"
          className={`${iconClassName} bg-current`}
          style={{
            WebkitMask: `url("${src}") center / contain no-repeat`,
            mask: `url("${src}") center / contain no-repeat`,
          }}
        />
      ) : (
        <img
          src={src}
          alt=""
          aria-hidden="true"
          draggable="false"
          className={`${iconClassName} object-contain`}
        />
      )}
    </span>
  );
}

function initialsFor(identity = {}) {
  return String(identity?.initials || "PT").slice(0, 2).toUpperCase();
}

function MemberAvatar({ identity, onImageError }) {
  if (identity?.avatarUrl) {
    return (
      <img
        src={identity.avatarUrl}
        alt=""
        className="h-full w-full object-cover"
        draggable="false"
        onError={onImageError}
      />
    );
  }

  return (
    <span className="text-label-medium font-medium text-[rgb(var(--afrimed-primary))]">
      {initialsFor(identity)}
    </span>
  );
}

function clsDesktopTab(active) {
  return [
    "h-9 px-1.5 lg:h-10 lg:px-2 flex items-center justify-center text-[13px] lg:text-body-medium border-b-2 transition",
    active
      ? "text-[rgb(var(--clr-on-surface))] border-[rgba(var(--clr-on-surface),0.88)]"
      : "border-transparent text-[rgb(var(--clr-on-surface-variant))] hover:text-[rgb(var(--clr-on-surface))]",
  ].join(" ");
}

function MobileNavButton({ label, icon, active = false, disabled = false, onClick }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={[
        "group flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left transition",
        active
          ? "bg-[rgba(var(--afrimed-primary),0.10)] text-[rgb(var(--clr-on-surface))]"
          : "text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]",
        disabled ? "cursor-not-allowed opacity-45 hover:bg-transparent" : "",
      ].join(" ")}
    >
      {typeof icon === "function" ? icon({ active }) : icon}
      <span className="flex-1 text-body-large">{label}</span>
      {disabled ? <Lock className="h-6 w-6 text-[rgb(var(--clr-on-surface-variant))]" aria-hidden /> : null}
    </button>
  );
}

function AccountMenuItem({ label, icon, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={[
        "flex w-full items-center gap-3 px-4 py-3 text-left text-body-medium",
        disabled
          ? "cursor-not-allowed text-[rgb(var(--clr-on-surface-variant))] opacity-45"
          : "text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]",
      ].join(" ")}
    >
      {icon}
      <span className="flex-1">{label}</span>
    </button>
  );
}

function ThemeOptionButton({ active, label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex w-full items-center gap-3 rounded-[12px] px-3 py-2 text-left text-body-medium transition",
        active
          ? "bg-[rgba(var(--afrimed-primary),0.10)] text-[rgb(var(--clr-on-surface))]"
          : "text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]",
      ].join(" ")}
    >
      <Icon className="h-[18px] w-[18px] text-[rgb(var(--clr-on-surface-variant))]" aria-hidden />
      <span className="flex-1">{label}</span>
      <span className={[
        "h-2.5 w-2.5 rounded-full border",
        active ? "border-[rgb(var(--afrimed-primary))] bg-[rgb(var(--afrimed-primary))]" : "border-[rgba(var(--clr-outline),0.9)] bg-transparent",
      ].join(" ")} />
    </button>
  );
}

const THEME_OPTIONS = [
  { value: "dark", label: "Dark", icon: Moon },
  { value: "light", label: "Light", icon: Sun },
  { value: "system", label: "System", icon: Monitor },
];

function sectionIcon(sectionId) {
  const icons = {
    home: ICON_HOME,
    tasks: ICON_TASKS,
    appointments: ICON_APPOINTMENTS,
    messages: ICON_MESSAGES,
    patients: ICON_PATIENTS,
    practice: ICON_PRACTICE,
    notifications: ICON_NOTIFICATIONS,
    billing: ICON_BILLING,
  };
  const visualScales = {
    messages: "scale-[1.08]",
    patients: "scale-[1.24]",
    notifications: "scale-[1.28]",
  };

  return ({ active = false, disabled = false } = {}) => (
    <AssetIcon
      src={icons[sectionId] || ICON_HOME}
      active={active}
      disabled={disabled}
      visualScale={visualScales[sectionId] || ""}
    />
  );
}

const IMPLEMENTED_ROUTES = new Set(["home", "tasks", "appointments", "messages", "patients"]);

export default function PracticeTeamWorkspaceHeader({ section = "home", invite = null, identity: identityProp = null }) {
  const auth = useAuth() || {};
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [accountOpen, setAccountOpen] = React.useState(false);
  const [loggingOut, setLoggingOut] = React.useState(false);
  const [logoutOpen, setLogoutOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const accountMenuRef = React.useRef(null);
  const baseIdentity = identityProp || getPracticeTeamMemberIdentity(invite || {}, auth?.user || {});
  const memberEmail = String(baseIdentity?.email || "").trim().toLowerCase();
  const memberUserId = String(auth?.user?.id || "").trim();
  const authenticatedEmail = String(auth?.user?.email || "").trim().toLowerCase();
  // A delegated-member row must never resolve its image from the user who
  // happens to own another workspace in this browser. The member account is
  // only safe to hydrate when this header identity is the authenticated member.
  const authenticatedMember = memberEmail && authenticatedEmail && memberEmail === authenticatedEmail
    ? auth?.user
    : null;
  const authenticatedProfileAvatar = authenticatedMember
    ? String(auth?.profile?.avatar_url || auth?.profile?.profile_image_url || "").trim()
    : "";
  const { photoUrl, refresh: refreshMemberPhoto } = usePracticeTeamMemberAccount({ user: authenticatedMember, identity: baseIdentity });
  const [failedAvatarUrl, setFailedAvatarUrl] = React.useState("");
  const identity = React.useMemo(
    () => ({
      ...baseIdentity,
      avatarUrl:
        photoUrl ||
        (baseIdentity.avatarUrl === failedAvatarUrl ? "" : baseIdentity.avatarUrl) ||
        (authenticatedProfileAvatar === failedAvatarUrl ? "" : authenticatedProfileAvatar),
    }),
    [authenticatedProfileAvatar, baseIdentity, failedAvatarUrl, photoUrl],
  );
  const retryMemberPhoto = React.useCallback(() => {
    if (identity?.avatarUrl) setFailedAvatarUrl(identity.avatarUrl);
    refreshMemberPhoto?.();
  }, [identity?.avatarUrl, refreshMemberPhoto]);

  const sections = React.useMemo(() => getPracticeTeamNavigation(invite?.role), [invite?.role]);
  const navItems = React.useMemo(
    () =>
      sections.map((item) => ({
        ...item,
        routeReady: IMPLEMENTED_ROUTES.has(item.id),
        icon: sectionIcon(item.id),
      })),
    [sections],
  );
  const desktopTabs = navItems.filter((item) => ["tasks", "appointments", "messages", "patients"].includes(item.id));

  React.useEffect(() => {
    setMobileOpen(false);
    setAccountOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  React.useEffect(() => {
    if (!accountOpen) return undefined;

    function handlePointerDown(event) {
      if (!accountMenuRef.current) return;
      if (!accountMenuRef.current.contains(event.target)) {
        setAccountOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setAccountOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [accountOpen]);

  function handleNavigate(to) {
    setMobileOpen(false);
    setAccountOpen(false);
    nav(to);
  }

  function submitSearch(event) {
    event?.preventDefault?.();
    setMobileOpen(false);
  }

  async function handleSignOut() {
    if (loggingOut) return;
    setLoggingOut(true);
    await logout({ redirectTo: "/login?logout=1" });
  }

  function requestSignOut() {
    setAccountOpen(false);
    setLogoutOpen(true);
  }

  async function handleSelectTheme(nextTheme) {
    setTheme(nextTheme);
    if (!memberUserId) return;
    try {
      await updatePharmacyMemberThemePreference({ userId: memberUserId, themePreference: nextTheme });
    } catch {
      // Keep the selected local theme even if the optional member preference write retries later.
    }
  }

  return (
    <>
      <div className="sticky top-0 z-40 w-full border-b border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))]">
        <div className="md:hidden">
          <div className="afrimed-rail afrimed-rail--comfortable [--rail-pad:12px] pt-4 pb-3 lg:[--rail-pad:var(--afrimed-rail-gutter-desktop)]">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                aria-label={t("settings.practiceTeam.nav.open", "Open Practice Team navigation")}
              >
                <Menu className="h-6 w-6 text-[rgb(var(--clr-on-surface))]" aria-hidden />
              </button>

              <button
                type="button"
                onClick={() => handleNavigate("/practice-team/welcome")}
                className="inline-flex min-w-0 flex-1 items-center"
                aria-label={t("settings.practiceTeam.nav.brand", "AfriMed Practice Team")}
              >
                <img src={BRAND_LOGO} alt="AfriMed" className="h-7 w-auto" draggable="false" />
              </button>

              <button
                type="button"
                onClick={() => handleNavigate("/practice-team/welcome")}
                className="relative grid h-10 w-10 shrink-0 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                aria-label={t("settings.practiceTeam.nav.notifications", "Notifications")}
              >
                <AssetIcon src={ICON_NOTIFICATIONS} visualScale="scale-[1.28]" />
              </button>
            </div>

            <form className="mt-4" onSubmit={submitSearch} role="search" aria-label={t("common.search", "Search")}>
              <button type="submit" className="sr-only">{t("common.search", "Search")}</button>
              <AfrimedSearchBar
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                onClear={() => setSearchValue("")}
                placeholder={t("settings.practiceTeam.nav.search", "Search tasks, appointments, messages")}
                ariaLabel={t("common.search", "Search")}
                className="bg-[rgba(var(--clr-on-surface-variant),0.08)] border-transparent"
                showTrailing={false}
              />
            </form>
          </div>
        </div>

        <div className="hidden md:block">
          <div className="afrimed-rail afrimed-rail--comfortable">
            <div className="flex h-14 items-center gap-4 lg:gap-6">
              <button
                type="button"
                onClick={() => handleNavigate("/practice-team/welcome")}
                className="inline-flex items-center"
                aria-label={t("settings.practiceTeam.nav.brand", "AfriMed Practice Team")}
              >
                <img src={BRAND_LOGO} alt="AfriMed" className="h-8 w-auto" draggable="false" />
              </button>

              <nav className="flex min-w-0 shrink-0 items-center gap-2 lg:gap-4" aria-label={t("settings.practiceTeam.nav.workspace", "Practice Team workspace")}>
                {desktopTabs.map((item) => {
                  const active = section === item.id || location.pathname === item.path;
                  const routeEnabled = item.enabled && item.routeReady;
                  return routeEnabled ? (
                    <NavLink key={item.id} to={item.path} className={() => clsDesktopTab(active)}>
                      {item.label}
                    </NavLink>
                  ) : (
                    <span key={item.id} className={`${clsDesktopTab(false)} cursor-not-allowed opacity-45`}>
                      {item.label}
                    </span>
                  );
                })}
              </nav>

              <form className="ml-auto hidden w-full max-w-[300px] shrink-0 md:block" onSubmit={submitSearch} role="search" aria-label={t("common.search", "Search")}>
                <button type="submit" className="sr-only">{t("common.search", "Search")}</button>
                <AfrimedSearchBar
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  onClear={() => setSearchValue("")}
                  placeholder={t("settings.practiceTeam.nav.search", "Search tasks, appointments, messages")}
                  ariaLabel={t("common.search", "Search")}
                  className="bg-[rgba(var(--clr-on-surface-variant),0.08)] border-transparent"
                  inputClassName="text-[13px] lg:text-label-medium"
                  showTrailing={false}
                />
              </form>

              <div ref={accountMenuRef} className="relative ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleNavigate("/practice-team/welcome")}
                  className="grid h-9 w-9 place-items-center rounded-xl hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  aria-label={t("settings.practiceTeam.nav.notifications", "Notifications")}
                >
                  <AssetIcon src={ICON_NOTIFICATIONS} visualScale="scale-[1.28]" />
                </button>

                <button
                  type="button"
                  onClick={() => setAccountOpen((value) => !value)}
                  className="flex h-11 flex-col items-center justify-center rounded-[12px] px-1.5 hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  aria-label={t("settings.practiceTeam.nav.meMenu", "Me menu")}
                >
                  <span className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-[rgba(29,79,215,0.10)]">
                    <MemberAvatar identity={identity} onImageError={retryMemberPhoto} />
                  </span>
                  <span className="mt-0.5 text-label-small text-[rgb(var(--clr-on-surface-variant))]">
                    {t("pharmacy.header.me", "Me")}
                  </span>
                </button>

                {accountOpen ? (
                  <div className="absolute right-0 top-[calc(100%+8px)] z-[60] w-[300px] overflow-hidden rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]">
                    <AccountMenuItem
                      label={t("settings.practiceTeam.nav.myAccount", "My account")}
                      icon={<AssetIcon src={ICON_ACCOUNT} visualScale="scale-[1.24]" mask />}
                      onClick={() => handleNavigate("/practice-team/account")}
                    />
                    <AccountMenuItem
                      label={t("settings.practiceTeam.nav.help", "Help")}
                      icon={<AssetIcon src={ICON_HELP} visualScale="scale-[1.08]" />}
                      onClick={() => handleNavigate("/help-support")}
                    />
                    <AccountMenuItem
                      label={t("pharmacy.header.reportIssue", "Make suggestion")}
                      icon={<AssetIcon src={ICON_SUGGESTION} visualScale="scale-[1.08]" />}
                      onClick={() => handleNavigate(`/feedback?from=${encodeURIComponent(location.pathname)}`)}
                    />
                    <AccountMenuItem
                      label={t("pharmacy.nav.settings", "Settings")}
                      icon={<AssetIcon src={ICON_SETTINGS} disabled visualScale="scale-[1.16]" />}
                      disabled
                    />
                    <div className="border-y border-[rgba(var(--clr-outline-variant),0.55)] px-4 py-3">
                      <p className="text-body-medium text-[rgb(var(--clr-on-surface-variant))]">Theme</p>
                      <div className="mt-2 space-y-1">
                        {THEME_OPTIONS.map((option) => (
                          <ThemeOptionButton
                            key={option.value}
                            active={theme === option.value}
                            label={option.label}
                            icon={option.icon}
                            onClick={() => handleSelectTheme(option.value)}
                          />
                        ))}
                      </div>
                    </div>
                    <AccountMenuItem
                      label={loggingOut ? t("pharmacy.header.signingOut", "Signing out…") : t("pharmacy.header.signOut", "Sign out")}
                      icon={<LogOut className="h-6 w-6 text-[rgb(var(--clr-on-surface-variant))]" aria-hidden />}
                      onClick={requestSignOut}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {mobileOpen && typeof document !== "undefined"
        ? createPortal(
            <div className="md:hidden">
              <div
                className="fixed inset-0 z-[70] bg-[rgba(15,23,42,0.42)] backdrop-blur-[6px]"
                onClick={() => setMobileOpen(false)}
              />
              <aside
                className="fixed inset-y-0 left-0 z-[71] flex w-[min(88vw,336px)] flex-col bg-[rgb(var(--clr-surface))] shadow-[0px_24px_48px_-20px_rgba(26,27,35,0.22)]"
                aria-label={t("settings.practiceTeam.nav.workspace", "Practice Team workspace")}
              >
                <div className="flex items-center justify-between px-4 pt-4 pb-3">
                  <button
                    type="button"
                    onClick={() => handleNavigate("/practice-team/welcome")}
                    className="inline-flex items-center"
                    aria-label={t("settings.practiceTeam.nav.brand", "AfriMed Practice Team")}
                  >
                    <img src={BRAND_LOGO} alt="AfriMed" className="h-7 w-auto" draggable="false" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="grid h-10 w-10 place-items-center rounded-[12px] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                    aria-label={t("settings.practiceTeam.nav.close", "Close navigation")}
                  >
                    <X className="h-5 w-5 text-[rgb(var(--clr-on-surface-variant))]" aria-hidden />
                  </button>
                </div>

                <div className="border-y border-[rgba(var(--clr-outline-variant),0.4)] px-4 py-3">
                  <form onSubmit={submitSearch} role="search" aria-label={t("common.search", "Search")}>
                    <button type="submit" className="sr-only">{t("common.search", "Search")}</button>
                    <AfrimedSearchBar
                      value={searchValue}
                      onChange={(event) => setSearchValue(event.target.value)}
                      onClear={() => setSearchValue("")}
                      placeholder={t("settings.practiceTeam.nav.search", "Search tasks, appointments, messages")}
                      ariaLabel={t("common.search", "Search")}
                      className="bg-[rgba(var(--clr-on-surface-variant),0.08)] border-transparent"
                      showTrailing={false}
                    />
                  </form>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-5">
                  <div className="mb-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-title-small text-[rgb(var(--clr-on-surface))]">
                          {t("settings.practiceTeam.nav.notifications", "Notifications")}
                        </p>
                        <p className="mt-1 text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                          {t("settings.practiceTeam.nav.notificationsCopy", "Delegated workflow alerts will appear here when enabled.")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    {navItems.map((item) => {
                      const active = section === item.id || location.pathname === item.path;
                      const routeEnabled = item.enabled && item.routeReady;
                      return (
                        <MobileNavButton
                          key={item.id}
                          label={item.label}
                      icon={item.icon({ active })}
                          active={active}
                          disabled={!routeEnabled}
                          onClick={() => handleNavigate(item.path)}
                        />
                      );
                    })}
                    <MobileNavButton
                      label={t("settings.practiceTeam.nav.help", "Help")}
                      icon={<AssetIcon src={ICON_HELP} visualScale="scale-[1.08]" />}
                      onClick={() => handleNavigate("/help-support")}
                    />
                  </div>
                </div>

                <div className="border-t border-[rgba(var(--clr-outline-variant),0.4)] px-4 py-4">
                  <div className="mb-4 flex items-center gap-3 border-b border-[rgba(var(--clr-outline-variant),0.4)] pb-4">
                    <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-[rgba(29,79,215,0.10)]">
                      <MemberAvatar identity={identity} onImageError={retryMemberPhoto} />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-body-medium text-[rgb(var(--clr-on-surface))]">
                        {identity.displayName || t("pharmacy.header.me", "Me")}
                      </p>
                      <p className="text-body-small text-[rgb(var(--clr-on-surface-variant))]">
                        {t("settings.practiceTeam.nav.workspaceRole", "Practice Team workspace")}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleNavigate("/practice-team/account")}
                    className="mb-1 flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left text-body-large text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  >
                    <AssetIcon src={ICON_ACCOUNT} visualScale="scale-[1.24]" mask />
                    <span>{t("settings.practiceTeam.nav.myAccount", "My account")}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleNavigate(`/feedback?from=${encodeURIComponent(location.pathname)}`)}
                    className="mb-1 flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left text-body-large text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  >
                    <AssetIcon src={ICON_SUGGESTION} visualScale="scale-[1.08]" />
                    <span>{t("pharmacy.header.reportIssue", "Make suggestion")}</span>
                  </button>

                  <button
                    type="button"
                    disabled
                    className="mb-1 flex w-full cursor-not-allowed items-center gap-3 rounded-[12px] px-4 py-3 text-left text-body-large text-[rgb(var(--clr-on-surface-variant))] opacity-45"
                  >
                    <AssetIcon src={ICON_SETTINGS} disabled visualScale="scale-[1.16]" />
                    <span>{t("pharmacy.nav.settings", "Settings")}</span>
                  </button>

                  <button
                    type="button"
                    onClick={requestSignOut}
                    className="flex w-full items-center gap-3 rounded-[12px] px-4 py-3 text-left text-body-large text-[rgb(var(--clr-on-surface))] hover:bg-[rgba(var(--clr-on-surface),0.06)]"
                  >
                    <LogOut className="h-6 w-6 text-[rgb(var(--clr-on-surface-variant))]" aria-hidden />
                    <span>{loggingOut ? t("pharmacy.header.signingOut", "Signing out…") : t("pharmacy.header.signOut", "Sign out")}</span>
                  </button>
                </div>
              </aside>
            </div>,
            document.body,
          )
        : null}
      {logoutOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              className="fixed inset-0 z-[220] flex items-end justify-center bg-[rgba(15,23,42,0.35)] px-3 pb-3 backdrop-blur-sm md:items-center md:px-0 md:pb-0"
              role="dialog"
              aria-modal="true"
              onClick={() => (loggingOut ? null : setLogoutOpen(false))}
            >
              <div
                className="w-full max-w-[420px] overflow-hidden rounded-[24px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_18px_45px_rgba(15,23,42,0.18)] md:rounded-2xl"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="p-5 md:p-6">
                  <div className="mx-auto flex w-full max-w-[320px] items-start justify-center gap-3">
                    <img src={ICON_LOG_OUT_CRITICAL} alt="" className="mt-0.5 h-6 w-6 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-title-medium text-on-surface">
                        {t("pharmacy.logout.confirmTitle", "Are you sure you want to log out?")}
                      </div>
                      <div className="mt-1 text-body-medium text-on-surface-var">
                        {t("pharmacy.logout.confirmBody", "You’ll need to sign in again to continue.")}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center justify-center gap-3">
                    <button
                      type="button"
                      className="flex-1 rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                      style={{
                        background: "var(--State-Layers-Primary-Opacity-08,rgba(29,79,215,0.08))",
                        color: "var(--Schemes-Primary,#1D4FD7)",
                      }}
                      disabled={loggingOut}
                      onClick={() => setLogoutOpen(false)}
                    >
                      {t("common.cancel", "Cancel")}
                    </button>

                    <button
                      type="button"
                      className="flex-1 rounded-xl px-4 py-3 text-label-large disabled:opacity-60"
                      style={{
                        background: "var(--State-Layers-Critical-Opacity-08,rgba(248,83,58,0.08))",
                        color: "var(--Schemes-Critical,#F8533A)",
                      }}
                      disabled={loggingOut}
                      onClick={handleSignOut}
                    >
                      {loggingOut
                        ? t("pharmacy.header.signingOut", "Signing out…")
                        : t("pharmacy.header.signOut", "Log out")}
                    </button>
                  </div>
                </div>

                <div className="h-px bg-[rgba(15,23,42,0.06)]" />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
